'use client';

import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { QuestionBar } from './question-bar';
import { QuestionBarMigrated } from './question-bar-migrated';
import { describeSubmitFailure } from './chat-submit-failure';
import { ChatEvidenceLockerProvider } from './chat-evidence-locker-context-new';
import { shouldUseOldChatNewEvidenceLocker } from './new-evidence-locker-flags';
import { evidenceAllFileTypesForCategoryScopes, categoryScopesFromAnswerOrPrev, excludedContentCategoriesFromScopes, evidenceCategoryScopeFieldsWithWireTokens } from './evidence-locker-ui-shared-new';
import { Welcome } from './welcome';
import {
  AnswerItem,
  Answer,
  EventFromChat,
  GenerateTimelineFromAnswerArgs,
  type AnswerTimelineGenerationStatus,
} from './answer-item';
import { captureError, captureWarning, getCaseInternalName, getTokenInfo, getUiSourceFromChatSource, toSearchablePath } from '@/shared/functions';
import { dedupeSources, useWindowSize } from '@/shared/clientFunctions';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import {
  caseFolderNameBody,
  useAwaitedCaseFolderNameResolver,
  useCaseFolderNameResolver,
  useCaseFolderName,
} from '@/hooks/useCaseFolderName';
import {
  AssistantApiError,
  deleteChatHistory,
  fetchChatHistory,
  postAssistantChat,
} from '@/shared/cors-assistant-api';
import {
  mobileAcquisitionIdForSubmit,
  resolveMobileAcquisitionIds,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  isMobileAnalysisAgentEnabled,
  isMobileAnalysisAvailableForCase,
} from '@/lib/mobile-analysis-agent';
import { isQuestionBarMigrationEnabled } from '@/lib/question-bar-migration';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { cacheChatHistoryItem, cacheChatHistoryItems } from '@/shared/chat-history-cache';
import { readStoredLocale } from '@/i18n/LocaleProvider';
import { responseLanguageForLocale } from '@/i18n/locales';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type { RefreshChatHistorySessionsOptions } from '@/shared/chat-archive-sessions';
import { pauseMediaElements } from '@/shared/pauseMediaElements';
import { MediaSource, Source, ScopesObject, Slide, FeatureFlagTypes, GetEventsFromEntityService } from '@/shared/types';
import { TINY_VIEWPORT_WIDTH } from '@/shared/constants';
import { Button } from '@tranquility-ai/theme-components/button';
import { GLOW_CTA_BUTTON_CLASS, glowCtaButtonProps } from './glow-cta-button';
import { ChatSessions, CHAT_SESSIONS_PANEL_WIDTH_PX } from './chat-sessions';
import {
  SidePanelShell,
  SIDE_PANEL_BACKDROP_CLASS,
  SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS,
  SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS,
} from './side-panel';
import { useSessionHistory } from './session-history-context';
import { AnimatePresence, motion } from 'framer-motion';
import { isPromptFlagOn } from '@/i18n/promptCatalog';
import { PromptSuggestionsNew } from './prompt-suggestions-new';
import { FullScreenContext, useReactiveAuthTokens } from '../app/contexts';
import { encodePreliminaryMessageStrings } from './preliminary-messages';
import {
  AnswerItemFromHistory,
  RETRY_MESSAGE,
  isTimelineHistoryItem,
  mapHistoryItemToAnswer,
} from '@/shared/map-chat-history-row';
import {
  answerMatchesPendingIdentity,
  historyRowMatchesPendingIdentity,
  isOptimisticServerCaughtUp,
  mergeFormattedAnswersWithOptimisticPending,
  parsePendingTurnIdentityFromAssistantResponse,
  type PendingTurnIdentity,
} from '@/shared/pending-chat-turn';

const ChatGettingStartedModal = dynamic(
  () => import('./chat-getting-started-modal').then((m) => m.ChatGettingStartedModal),
  { ssr: false, loading: () => null },
);

const assistantSkipScopeRestoreStorageKey = (caseId: string) => `${caseId}-assistantSkipScopeRestore`;

/** Keys that scroll the answers container when it or a descendant has focus. */
const CHAT_SCROLL_INTENT_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

/**
 * Fresh, empty evidence-locker state. The locker resets to this whenever the
 * loaded session has no history (new case, cleared thread, swapped-in empty
 * session). Chat history is the sole source of truth for the locker now —
 * there is no persisted snapshot.
 */
function emptyScopes(): ScopesObject {
  return {
    scopes: [],
    files: [],
    excludedScopes: [],
    excludedFiles: [],
    contentCategories: [],
    excludedContentCategories: [],
    evidenceCategoryChips: [],
    evidenceCategoryScopes: [],
    excludedEvidenceCategoryScopes: [],
    evidenceAllFileTypes: false,
  };
}

const PENDING_NEW_SESSION_STORAGE_PREFIX = 'tpl-pendingNewSession-';

function pendingNewSessionStorageKey(caseId: string): string {
  return `${PENDING_NEW_SESSION_STORAGE_PREFIX}${caseId}`;
}

/** Set after "Start New Session" until the first query in that fresh thread. */
function markPendingNewSession(caseId: string | undefined | null): void {
  if (typeof window === 'undefined' || !caseId) return;
  try {
    sessionStorage.setItem(pendingNewSessionStorageKey(caseId), '1');
  } catch {
    // non-fatal
  }
}

function isPendingNewSession(caseId: string | undefined | null): boolean {
  if (typeof window === 'undefined' || !caseId) return false;
  try {
    return sessionStorage.getItem(pendingNewSessionStorageKey(caseId)) === '1';
  } catch {
    return false;
  }
}

function clearPendingNewSession(caseId: string | undefined | null): void {
  if (typeof window === 'undefined' || !caseId) return;
  try {
    sessionStorage.removeItem(pendingNewSessionStorageKey(caseId));
  } catch {
    // non-fatal
  }
}

/**
 * Per-case scopes snapshot that survives navigation *while* the user is in a
 * "Start New Session" state with no submitted query yet. Chat history is the
 * normal source of truth for the locker, but a fresh thread has no history
 * yet — without this snapshot the user's edits in the pre-query bar would
 * vanish the moment they switch cases. Lifetime is strictly bounded by the
 * pending-new-session flag: cleared on query submit and on session switch.
 *
 * sessionStorage (not localStorage) so it disappears with the tab, matching
 * the ephemeral nature of an unsubmitted draft.
 */
const PENDING_NEW_SESSION_SCOPES_STORAGE_PREFIX = 'tpl-pendingNewSessionScopes-';

function pendingNewSessionScopesStorageKey(caseId: string): string {
  return `${PENDING_NEW_SESSION_SCOPES_STORAGE_PREFIX}${caseId}`;
}

function savePendingNewSessionScopes(
  caseId: string | undefined | null,
  scopes: ScopesObject,
): void {
  if (typeof window === 'undefined' || !caseId) return;
  try {
    sessionStorage.setItem(
      pendingNewSessionScopesStorageKey(caseId),
      JSON.stringify({
        scopes: scopes.scopes ?? [],
        files: scopes.files ?? [],
        excludedScopes: scopes.excludedScopes ?? [],
        excludedFiles: scopes.excludedFiles ?? [],
        contentCategories: scopes.contentCategories ?? [],
        excludedContentCategories: scopes.excludedContentCategories ?? [],
        evidenceCategoryChips: scopes.evidenceCategoryChips ?? [],
        excludedEvidenceCategoryScopes: scopes.excludedEvidenceCategoryScopes ?? [],
        evidenceCategoryScopes: scopes.evidenceCategoryScopes ?? [],
        evidenceAllFileTypes: !!scopes.evidenceAllFileTypes,
      }),
    );
  } catch {
    // non-fatal
  }
}

function loadPendingNewSessionScopes(
  caseId: string | undefined | null,
): ScopesObject | null {
  if (typeof window === 'undefined' || !caseId) return null;
  try {
    const raw = sessionStorage.getItem(pendingNewSessionScopesStorageKey(caseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const stringArray = (value: unknown): string[] =>
      Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
    return {
      scopes: stringArray(parsed.scopes),
      files: stringArray(parsed.files),
      excludedScopes: stringArray(parsed.excludedScopes),
      excludedFiles: stringArray(parsed.excludedFiles),
      contentCategories: stringArray(parsed.contentCategories),
      excludedContentCategories: stringArray(parsed.excludedContentCategories),
      evidenceCategoryChips: stringArray(parsed.evidenceCategoryChips),
      excludedEvidenceCategoryScopes: stringArray(parsed.excludedEvidenceCategoryScopes),
      evidenceCategoryScopes: stringArray(parsed.evidenceCategoryScopes),
      evidenceAllFileTypes: !!parsed.evidenceAllFileTypes,
    };
  } catch {
    return null;
  }
}

function clearPendingNewSessionScopes(caseId: string | undefined | null): void {
  if (typeof window === 'undefined' || !caseId) return;
  try {
    sessionStorage.removeItem(pendingNewSessionScopesStorageKey(caseId));
  } catch {
    // non-fatal
  }
}

/**
 * Adapt a chat-history echo into a locker snapshot for the next question.
 *
 * scopes / files / excludedScopes / excludedFiles are always taken from the
 * echoed answer (server is the source of truth for these).
 *
 * Category fields are taken from the answer when the server echoed them
 * (including explicit empty arrays, which clear filters). When a field is
 * omitted (``undefined``), keep ``prev`` so just-submitted selections survive
 * a refresh from a server that does not echo categories yet.
 */
function scopesObjectFromAnswerForLockerReuse(answer: ScopesObject, prev: ScopesObject): ScopesObject {
  const contentCategories = categoryScopesFromAnswerOrPrev(
    answer.contentCategories,
    prev.contentCategories,
  );
  const excludedContentCategories = categoryScopesFromAnswerOrPrev(
    answer.excludedContentCategories,
    prev.excludedContentCategories,
  );
  const evidenceCategoryChips = categoryScopesFromAnswerOrPrev(
    answer.evidenceCategoryChips,
    prev.evidenceCategoryChips,
  );
  const excludedEvidenceCategoryScopes = categoryScopesFromAnswerOrPrev(
    answer.excludedEvidenceCategoryScopes,
    prev.excludedEvidenceCategoryScopes,
  );
  const evidenceCategoryScopes = categoryScopesFromAnswerOrPrev(
    answer.evidenceCategoryScopes,
    prev.evidenceCategoryScopes,
  );

  return {
    ...prev,
    scopes: [...(answer.scopes ?? [])],
    files: [...(answer.files ?? [])],
    excludedScopes: [...(answer.excludedScopes ?? [])],
    excludedFiles: [...(answer.excludedFiles ?? [])],
    contentCategories,
    excludedContentCategories,
    evidenceCategoryChips,
    excludedEvidenceCategoryScopes,
    evidenceCategoryScopes,
    evidenceAllFileTypes: evidenceAllFileTypesForCategoryScopes(
      evidenceCategoryScopes,
      excludedEvidenceCategoryScopes,
      answer.evidenceAllFileTypes !== undefined
        ? !!answer.evidenceAllFileTypes
        : !!prev.evidenceAllFileTypes,
    ),
  };
}

// Map file types to BKB ObjectCategory values for content filtering
const fileTypeToCategoryMap: Record<string, string> = {
  'PDF': 'PDF',
  'MS Office': 'OFFICE',
  'Image': 'IMAGE',
  'Video': 'VIDEO',
  'Audio': 'AUDIO',
  'Spreadsheet': 'SPREADSHEET',
  'Email': 'EMAIL',
  'Other': 'OTHER',
};

interface ChatProps {
  /** When false, user is not on the Assistant tab (chat is hidden); used to restore locker from the last Q&A when they return. */
  assistantViewVisible?: boolean;
  /**
   * True while Assistant is the selected top-nav view. Distinct from
   * ``assistantViewVisible``, which also goes false for a fullscreen media overlay
   * opened *from* Assistant. Chat stays mounted when the user navigates away, so
   * the cleanups CSS hiding cannot perform — stopping playback, closing overlays
   * portaled to ``document.body`` — key off this instead.
   */
  assistantViewSelected?: boolean;
  selectedOption: string;
  userId: string;
  userName: string;
  updateNewAnswer: (newAnswer: Answer) => void;
  addToTimeline: (event: EventFromChat) => void;
  generateTimelineFromAnswer: (args: GenerateTimelineFromAnswerArgs) => void | Promise<void>;
  timelineGenerationStatuses: Record<string, AnswerTimelineGenerationStatus>;
  addMultipleToTimeline: (events: Slide[], useEnrichment: boolean, edit: boolean) => void;
  applyAutogeneratedTimelineFromAnswer: (answerText: string, epochTimeSecs: number) => void;
  isAutogeneratedTimelineAppliedForAnswer: (epochTimeSecs: number) => boolean;
  isAutogeneratedTimelineApplyingForAnswer: (epochTimeSecs: number) => boolean;
  isEditableTimeline: boolean;
  setSourcesFromChat: (sources: MediaSource[]) => void;
  swapSessionId: (sessionId: string | null, newSessionId: string) => Promise<string | null>;
  setMainChatSessionId: (sessionId: string | null) => void;
  /**
   * Nav restore target from URL/session/history.
   * `undefined` = no instruction; `null` = explicit clear; string = activate session.
   */
  initialChatSessionId?: string | null;
  /** Called after `initialChatSessionId` is applied so Main can clear the seed. */
  onNavChatSessionApplied?: () => void;
  entityExtractionJobs: any[];
  chatHistorySessions: any[] | null;
  refreshChatHistorySessions?: (
    options?: RefreshChatHistorySessionsOptions,
  ) => void | Promise<void>;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  getEventsFromEntityService: GetEventsFromEntityService;
  featureFlags: { [key: string]: FeatureFlagTypes };
}

/**
 * Human-readable diagnostic for a failed assistant request. Surfaces the HTTP
 * status and any response body carried by {@link AssistantApiError} so a POST
 * `/chat` that never reaches (or is rejected by) the service is logged with an
 * actual reason instead of being silently swallowed.
 */
function describeAssistantError(error: unknown): string {
  if (error instanceof AssistantApiError) {
    const parts: string[] = [error.message];
    if (error.status != null) {
      parts.push(`status=${error.status}`);
    }
    if (error.detail) {
      parts.push(`detail=${error.detail.slice(0, 500)}`);
    }
    return parts.join(' | ');
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function isRenderableAnswer(item: Answer): boolean {
  return !!item.pendingPlaceholder || !!item.answerText;
}

function answerRowKey(answer: Answer, index: number): string {
  if (answer.pendingPlaceholder) {
    return `pending-${answer.epochTimeSecs}`;
  }
  const chatKey =
    typeof answer.resultForFeedback?.chat_key === 'string'
      ? answer.resultForFeedback.chat_key
      : 'row';
  return `${chatKey}-${answer.epochTimeSecs}-${index}`;
}

/** Indices of answers that have a corresponding ``answer-item-*`` scroll anchor. */
function getRenderableAnswerIndices(answers: Answer[] | undefined): number[] {
  if (!answers?.length) return [];
  const indices: number[] = [];
  for (let i = 0; i < answers.length; i++) {
    if (isRenderableAnswer(answers[i])) {
      indices.push(i);
    }
  }
  return indices;
}

function getAnswerAnchorOffsetTop(anchor: HTMLElement, container: HTMLElement): number {
  return (
    anchor.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(
    'input, textarea, select, [contenteditable="true"], [role="textbox"]',
  );
}

/** Widgets that own ArrowUp/ArrowDown — do not steal keys for answer snapping. */
function isArrowKeyReservedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  // Open listboxes (including portaled case-menu panels) always keep arrows.
  if (target.closest('[role="listbox"]')) return true;

  if (isTypingTarget(target)) return true;

  return !!target.closest(
    [
      '[role="dialog"]',
      '[aria-modal="true"]',
      '[role="menu"]',
      '[role="menubar"]',
      '[role="combobox"]',
      '[role="option"]',
      '[role="menuitem"]',
      '[role="menuitemcheckbox"]',
      '[role="menuitemradio"]',
      '[role="tree"]',
      '[role="treeitem"]',
      '[role="grid"]',
      '[role="gridcell"]',
      '[role="slider"]',
      '[role="tablist"]',
      '[role="radiogroup"]',
      '[aria-haspopup][aria-expanded="true"]',
    ].join(', '),
  );
}

const ChatComponent: React.FC<ChatProps> = ({
  assistantViewVisible = true,
  assistantViewSelected = true,
  selectedOption,
  userName,
  userId,
  updateNewAnswer,
  addToTimeline,
  generateTimelineFromAnswer,
  timelineGenerationStatuses,
  addMultipleToTimeline,
  applyAutogeneratedTimelineFromAnswer,
  isAutogeneratedTimelineAppliedForAnswer,
  isAutogeneratedTimelineApplyingForAnswer,
  isEditableTimeline,
  setSourcesFromChat,
  swapSessionId,
  setMainChatSessionId,
  initialChatSessionId,
  onNavChatSessionApplied,
  entityExtractionJobs,
  chatHistorySessions,
  refreshChatHistorySessions,
  createEventsFromEntityEvents,
  getEventsFromEntityService,
  featureFlags,
}) => {
  const t = useTimePilotT();
  const personalFilesCaseId = usePersonalFilesCaseId();
  const { width } = useWindowSize();
  const isTinyViewport = width !== undefined && width < TINY_VIEWPORT_WIDTH;
  const [answers, setAnswers] = useState<undefined | Answer[]>(undefined);
  const chatRootRef = useRef<HTMLDivElement>(null);
  /** Hide answer rows until the initial tail snap finishes (avoids blank mid-scroll flash). */
  const [historyScrollPositioned, setHistoryScrollPositioned] = useState(false);
  /** Latest answers for async poll/load closures (see `commitRefreshedAnswers`). */
  const answersRef = useRef<undefined | Answer[]>(answers);
  answersRef.current = answers;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [previousSessionId, setPreviousSessionId] = useState<string | null>(null);
  const lastAppliedNavSessionRef = useRef<string | null | undefined>(undefined);
  const [query, setQuery] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null); // Create a ref for the textarea
  // const [totalTimeSaved, setTotalTimeSaved] = useState(0);
  // const [historyError, setHistoryError] = useState<string | null>(null);
  const [previousSelectedOption, setPreviousSelectedOption] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Define isExpanded state
  /**
   * User-facing error for a failed submit (e.g. session/cell missing, network).
   * Rendered inline beneath the question bar. Cleared at the start of each new
   * submit. We deliberately do NOT clear the typed question on failure so the
   * user can retry without retyping.
   */
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Keeps the question bar expanded (with frozen submit UI) until the new AnswerItem mounts. */
  const [holdQuestionBarExpanded, setHoldQuestionBarExpanded] = useState(false);
  const holdQuestionBarExpandedRef = useRef(false);
  const pendingSubmitEpochRef = useRef<number | null>(null);
  const pendingHistoryRefreshEpochRef = useRef<number | null>(null);
  /** Monotonic id per `onQuerySubmit` call; stale async completions must not release a newer hold. */
  const submitGenerationRef = useRef(0);
  const [showSources, setShowSources] = useState(false); // Define showSources state
  // const [processedSources, setProcessedSources] = useState<MediaSource[]>([]);
  const [processedSourcesMap, setProcessedSourcesMap] = useState<{ [key: number]: MediaSource[] }>({});
  const sessionHistory = useSessionHistory();
  const [localShowChatSessions, setLocalShowChatSessions] = useState<boolean>(false);
  const showChatSessions = sessionHistory ? sessionHistory.isOpen : localShowChatSessions;
  const [showFolders, setShowFolders] = useState(false);
  const [allFileTypes, setAllFileTypes] = useState(true);
  const [activeFileTypes, setActiveFileTypes] = useState({});
  const [evidenceFilter, setEvidenceFilter] = useState<string>('');
  const [scopesAndFiles, setScopesAndFiles] = useState<ScopesObject>(emptyScopes);
  /** Latest scopes for async merges (e.g. chat history lacks category fields). */
  const scopesAndFilesRef = useRef(scopesAndFiles);
  scopesAndFilesRef.current = scopesAndFiles;

  /** Avoid re-applying the same tail answer when ``answers`` reference churns. */
  const lastAssistantSyncedTailEpochRef = useRef<number | null>(null);
  const lastAssistantSyncedCaseRef = useRef<string | null>(null);
  const [extractionJobs, setExtractionJobs] = useState<any[]>([]);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);
  const [triggerPromptLibrary, setTriggerPromptLibrary] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [preliminaryResult, setPreliminaryResult] = useState<{query:string, answer:string} | null>(null);

  const { isFullscreen, setIsFullscreen, setFullscreenMediaItem } = useContext(FullScreenContext);

  const answersContainerRef = useRef<HTMLDivElement>(null);
  /** Chat collapses to 1×1px while fullscreen; restore scroll so SourceCard stays in view. */
  const fullscreenChatScrollRestoreRef = useRef<number | null>(null);
  const prevChatIsFullscreenRef = useRef(isFullscreen);
  const currentAnswerIndex = useRef<number | undefined>(undefined);
  const currentSessionId = useRef<string | null>(null);
  /**
   * After case/session history is (re)loaded, scroll to the last answer-item
   * (most recent question) before revealing the thread.
   */
  const needsInitialHistoryScrollRef = useRef(false);
  /** After submit, keep snapping to the new turn until the user scrolls the thread. */
  const autoSnapScrollRef = useRef(false);
  /** Suppress scroll-end snap while a programmatic snap animation is in flight. */
  const programmaticSnapRef = useRef(false);
  const scrollSnapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * When true, ArrowUp/Down navigate answer items instead of the case Combobox.
   * Armed by interacting with chat; disarmed by leaving Assistant or clicking the nav.
   */
  const chatOwnsArrowNavRef = useRef(false);
  const optimisticPendingRef = useRef<Answer | null>(null);
  const optimisticPendingIdentityRef = useRef<PendingTurnIdentity | null>(null);
  const chatRequestsInFlightRef = useRef(0);
  const loadChatHistoryGenerationRef = useRef(0);
  // Tracks the live case so async `loadChatHistory` closures captured for an
  // older case can detect they are stale and refuse to commit their results.
  const selectedOptionRef = useRef(selectedOption);

  // For `/chat/history` calls that read the case out of `selectedOptionRef` at
  // call time instead of closing over the render's selection — those must key on
  // *that* case's segment, not on whichever case is selected by the time they
  // run. See `useCaseFolderName`.
  const resolveCaseFolderName = useCaseFolderNameResolver();
  // Read at call time, alongside `selectedOptionRef`: the resolver returned by a
  // render before the cases list landed resolves nothing. A follow-up read that
  // fires after an awaited write — which resolves the segment by fetching that
  // same cases list — would otherwise still be holding that empty resolver and
  // would read the display-name partition the write deliberately avoided.
  const resolveCaseFolderNameRef = useRef(resolveCaseFolderName);
  resolveCaseFolderNameRef.current = resolveCaseFolderName;

  const mergeOptimisticPending = useCallback((formatted: Answer[]): Answer[] => {
    const pending = optimisticPendingRef.current;
    const identity = optimisticPendingIdentityRef.current;
    if (
      pending?.pendingPlaceholder
      && isOptimisticServerCaughtUp(formatted, pending, identity)
    ) {
      optimisticPendingRef.current = null;
      optimisticPendingIdentityRef.current = null;
    }
    return mergeFormattedAnswersWithOptimisticPending(
      formatted,
      pending,
      identity,
    );
  }, []);

  /**
   * One-shot per-epoch `/chat/history` fetch for a turn the assistant POST
   * just assigned. Ongoing refresh until terminal status is owned by
   * {@link AnswerItem} per-row polling.
   */
  const refreshPendingHistoryItem = useCallback(
    async (identity: PendingTurnIdentity): Promise<Answer[]> => {
      const generation = loadChatHistoryGenerationRef.current;
      const requestedOption = selectedOptionRef.current;
      const accessTokenForLoad = realAccessTokenRef.current;
      const idTokenForLoad = idTokenRef.current;
      const cellId = resolveCellIdFromIdToken(idTokenForLoad);
      const commitRefreshedAnswers = (list: Answer[]): Answer[] => {
        const merged = mergeOptimisticPending(list);
        const tail = merged.length > 0 ? merged[merged.length - 1] : null;
        if (tail && !tail.pendingPlaceholder) {
          setSessionId(tail.sessionId);
          setMainChatSessionId(tail.sessionId);
        }
        setAnswers(merged);
        answersRef.current = merged;
        return merged;
      };
      /** List already includes the optimistic row — do not re-merge. */
      const preserveCurrent = (): Answer[] => answersRef.current ?? [];
      if (!accessTokenForLoad || !cellId || !requestedOption) {
        return preserveCurrent();
      }
      try {
        const history = await fetchChatHistory({
          cellId,
          accessToken: accessTokenForLoad,
          idToken: idTokenForLoad,
          body: {
            userId: userId,
            caseId: requestedOption,
            ...caseFolderNameBody(resolveCaseFolderNameRef.current(requestedOption)),
            epochTimeSecs: identity.epochTimeSecs,
          },
        });
        if (
          generation !== loadChatHistoryGenerationRef.current ||
          requestedOption !== selectedOptionRef.current
        ) {
          return preserveCurrent();
        }
        const rows = Array.isArray(history)
          ? (history as AnswerItemFromHistory[])
          : [];
        const row = rows.find((item) =>
          !isTimelineHistoryItem(item) && historyRowMatchesPendingIdentity(item, identity),
        );
        if (!row) {
          return preserveCurrent();
        }
        const mapped = mapHistoryItemToAnswer(row, t);
        if (!mapped) {
          return preserveCurrent();
        }
        cacheChatHistoryItem(row);
        const list = [...(answersRef.current ?? [])];
        let matchIdx = list.findIndex(
          (answer) =>
            !answer.pendingPlaceholder
            && answerMatchesPendingIdentity(answer, identity),
        );
        if (matchIdx < 0) {
          const optimisticEpoch = optimisticPendingRef.current?.epochTimeSecs;
          matchIdx = list.findIndex(
            (answer) =>
              answer.pendingPlaceholder
              && (answer.resultForFeedback?.epoch_time_secs
                === identity.epochTimeSecs
                || (typeof optimisticEpoch === 'number'
                  && answer.epochTimeSecs === optimisticEpoch)),
          );
        }
        if (matchIdx >= 0) {
          const prevScopes = list[matchIdx]?.scopesObject ?? emptyScopes();
          list[matchIdx] = {
            ...mapped,
            scopesObject: scopesObjectFromAnswerForLockerReuse(
              mapped.scopesObject,
              prevScopes,
            ),
          };
        } else {
          list.push(mapped);
        }
        return commitRefreshedAnswers(list);
      } catch {
        return preserveCurrent();
      }
    },
    [userId, mergeOptimisticPending, setMainChatSessionId],
  );

  // Reactive so a memoized Chat still re-renders on a Cognito token refresh
  // (rare) while skipping the frequent unrelated `Main` re-renders.
  const { idToken, accessToken, realAccessToken } = useReactiveAuthTokens();
  const realAccessTokenRef = useRef(realAccessToken);
  const idTokenRef = useRef(idToken);
  realAccessTokenRef.current = realAccessToken;
  idTokenRef.current = idToken;

  /** Case id for chat API: ``*`` stays ``*`` for “All Cases” (not the display label). */
  const selectedCaseForAssistant = useMemo(() => {
    if (!selectedOption || selectedOption.trim() === '') return selectedOption;
    const s = selectedOption.trim();
    if (s === '*' || s === 'All Cases') return '*';
    return s;
  }, [selectedOption]);

  // Sent alongside `selectedCase` so retrieval filters on the case's real tag
  // rather than a `case:<display name>` guess, which matches nothing for a
  // UUID-folder case. See `useCaseFolderName`.
  const caseFolderName = useCaseFolderName(selectedCaseForAssistant);
  // Reads above may sample a still-loading cases list and degrade to the display
  // name; a send may not — see `useAwaitedCaseFolderNameResolver`.
  const awaitCaseFolderName = useAwaitedCaseFolderNameResolver();

  const handleShowChatSessions = (show: boolean) => {
    if (sessionHistory) {
      if (show) sessionHistory.open();
      else sessionHistory.close();
    } else {
      setLocalShowChatSessions(show);
    }
  };

  const applyPersistedContentCategoryState = useCallback(
    (_saved: ScopesObject) => {
      // Locker browse filters are independent of filter-picker content categories.
      setActiveFileTypes({});
      setAllFileTypes(false);
    },
    [],
  );

  /**
   * User-edit entry point for the locker bar. Behaves like `setScopesAndFiles`
   * but, while the case is in a pending-new-session state (post "Start New
   * Session", pre first submitted query), also mirrors every change into the
   * per-case sessionStorage snapshot. This is the only continuity source for
   * the fresh-thread bar — chat history can't supply it because no query has
   * been submitted yet. Once the user submits a query the snapshot is
   * cleared and chat history resumes as the source of truth.
   *
   * Programmatic resets (case-switch, applyLockerForCase, assistant-sync,
   * etc.) intentionally use the raw `setScopesAndFiles` so they never write
   * to the snapshot.
   *
   * Persistence is gated on `next !== prev`: the question-bar /
   * evidence-locker hooks run "mirror" updater effects that return `prev`
   * unchanged when nothing real has happened. Persisting those would clobber
   * the preserved snapshot whenever the case-switch effect transiently resets
   * the bar to empty before `loadChatHistory` restores it.
   */
  const handleSetScopesAndFiles = useCallback(
    (update: ScopesObject | ((prev: ScopesObject) => ScopesObject)) => {
      setScopesAndFiles((prev) => {
        const next =
          typeof update === 'function'
            ? (update as (p: ScopesObject) => ScopesObject)(prev)
            : update;
        if (next !== prev && isPendingNewSession(selectedOption)) {
          savePendingNewSessionScopes(selectedOption, next);
        }
        return next;
      });
    },
    [selectedOption],
  );

  const addSourceToScope = (path: string) => {
    handleSetScopesAndFiles((prev) => ({
      ...prev,
      files: Array.from(new Set([...prev.files, path])),
    }));
  };

  useEffect(() => {
    setExtractionJobs(entityExtractionJobs);
  }, [entityExtractionJobs]);

  useEffect(() => {
    // Case switch: clear the question bar text immediately. `loadChatHistory`
    // will then restore the new case's locker scopes from chat history (or, if
    // the new case is in a pending-new-session state, from its preserved
    // pre-query scope snapshot). Intentionally do NOT clearPendingNewSession
    // here: if the user started a new session on case A, edited the bar, then
    // visited case B and came back to A, the pending state on A must still be in
    // effect so scopes can be re-restored from the snapshot.
    const fresh = emptyScopes();
    selectedOptionRef.current = selectedOption;
    lastAssistantSyncedTailEpochRef.current = null;
    lastAssistantSyncedCaseRef.current = null;
    optimisticPendingRef.current = null;
    optimisticPendingIdentityRef.current = null;
    autoSnapScrollRef.current = false;
    currentAnswerIndex.current = undefined;
    currentSessionId.current = null;
    chatRequestsInFlightRef.current = 0;
    setIsLoading(false);
    setQuery('');
    if (textareaRef.current) {
      textareaRef.current.value = '';
    }
    setScopesAndFiles(fresh);
    applyPersistedContentCategoryState(fresh);
    setIsExpanded(false);
    holdQuestionBarExpandedRef.current = false;
    setHoldQuestionBarExpanded(false);
    pendingSubmitEpochRef.current = null;
    pendingHistoryRefreshEpochRef.current = null;
    setIsFullscreen(false);
    setPreliminaryResult(null);
    answersRef.current = undefined;
    setAnswers(undefined);
    needsInitialHistoryScrollRef.current = true;
    setHistoryScrollPositioned(false);
    setTriggerPromptLibrary(0);
    handleShowChatSessions(false);
  }, [selectedOption, applyPersistedContentCategoryState]);

  /** Scroll the answers list so the last renderable answer-item is in view. */
  const scrollToLastAnswerItem = useCallback((list: Answer[]): boolean => {
    const container = answersContainerRef.current;
    if (!container) return false;
    const indices = getRenderableAnswerIndices(list);
    if (indices.length === 0) {
      container.scrollTop = 0;
      currentAnswerIndex.current = undefined;
      return true;
    }
    const targetIndex = indices[indices.length - 1];
    const anchor = document.getElementById(`answer-item-${targetIndex}`);
    if (!anchor) {
      // DOM not ready — pin to bottom as a best-effort fallback.
      container.scrollTop = container.scrollHeight;
      currentAnswerIndex.current = targetIndex;
      return false;
    }
    programmaticSnapRef.current = true;
    container.scrollTop = getAnswerAnchorOffsetTop(anchor, container);
    currentAnswerIndex.current = targetIndex;
    window.setTimeout(() => {
      programmaticSnapRef.current = false;
    }, 0);
    return true;
  }, []);

  useEffect(() => {
    if (answers === undefined) {
      setHistoryScrollPositioned(false);
      return;
    }
    if (answers.length === 0) {
      needsInitialHistoryScrollRef.current = false;
      setHistoryScrollPositioned(true);
      return;
    }
    // Safety net: if initial positioning hasn't finished, force scroll to the
    // last answer-item so Back/Forward case loads never leave the user mid-list
    // or stuck on "Loading case history…".
    const t = window.setTimeout(() => {
      if (!needsInitialHistoryScrollRef.current) {
        setHistoryScrollPositioned(true);
        return;
      }
      scrollToLastAnswerItem(answers);
      needsInitialHistoryScrollRef.current = false;
      setHistoryScrollPositioned(true);
    }, 400);
    return () => clearTimeout(t);
  }, [answers, scrollToLastAnswerItem]);

  // When returning to Assistant (or when a new tail answer appears), mirror the last Q&A's evidence filters into the locker for the next question.
  useEffect(() => {
    if (!assistantViewVisible || !selectedOption) return;
    if (typeof window !== 'undefined' && sessionStorage.getItem(assistantSkipScopeRestoreStorageKey(selectedOption))) {
      return;
    }
    const list = answers?.filter((a) => isRenderableAnswer(a) && !a.pendingPlaceholder) ?? [];
    if (!list.length) return;
    const last = list[list.length - 1];
    const tailEpoch = last.epochTimeSecs;
    if (
      lastAssistantSyncedCaseRef.current === selectedOption &&
      lastAssistantSyncedTailEpochRef.current === tailEpoch
    ) {
      return;
    }
    lastAssistantSyncedCaseRef.current = selectedOption;
    lastAssistantSyncedTailEpochRef.current = tailEpoch;
    const next = scopesObjectFromAnswerForLockerReuse(last.scopesObject, emptyScopes());
    setScopesAndFiles(next);
    applyPersistedContentCategoryState(next);
  }, [assistantViewVisible, answers, selectedOption, applyPersistedContentCategoryState]);

  useEffect(() => {
    const handler = (e: CustomEvent<{ caseId: string }>) => {
      if (e.detail?.caseId === selectedOption) {
        const sessionSaved = JSON.parse(
          sessionStorage.getItem(`${selectedOption}-scopesObject`) ||
            '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }',
        ) as ScopesObject;
        setScopesAndFiles(sessionSaved);
        applyPersistedContentCategoryState(sessionSaved);
      }
    };
    window.addEventListener('scopes-updated', handler as EventListener);
    return () => window.removeEventListener('scopes-updated', handler as EventListener);
  }, [selectedOption, applyPersistedContentCategoryState]);

  const switchToSession = async (newSessionId: string) => {
    optimisticPendingRef.current = null;
    optimisticPendingIdentityRef.current = null;
    autoSnapScrollRef.current = false;
    currentAnswerIndex.current = undefined;
    currentSessionId.current = null;
    answersRef.current = undefined;
    setAnswers(undefined);
    needsInitialHistoryScrollRef.current = true;
    setHistoryScrollPositioned(false);
    handleShowChatSessions(false);
    setTriggerPromptLibrary(0);
    // Clear locker state up front so the prior session's scopes never leak
    // into the new session. `loadChatHistory` will then repopulate the locker
    // from the target session's last answer (chat history is the sole
    // source of truth).
    const fresh = emptyScopes();
    lastAssistantSyncedTailEpochRef.current = null;
    lastAssistantSyncedCaseRef.current = null;
    clearPendingNewSession(selectedOption);
    clearPendingNewSessionScopes(selectedOption);
    setScopesAndFiles(fresh);
    applyPersistedContentCategoryState(fresh);
    const oldSessionId = sessionId;
    setIsSwapping(true);
    try {
      // Do NOT setSessionId(null) before the swap — that fires the
      // loadChatHistory useEffect against the still-old session.
      const sessionReturned = await swapSessionId(oldSessionId, newSessionId);
      setPreviousSessionId('SWAP');
      setSessionId(sessionReturned ?? null);
    } catch (error) {
      captureError(
        `Error switching chat session for case ${selectedOption}`,
        error as Error,
      );
      setPreviousSessionId('SWAP');
      setSessionId(newSessionId);
    } finally {
      setIsSwapping(false);
    }
  };

  const handleCloseSessions = () => {
    handleShowChatSessions(false);
  };

  useEffect(() => {
    if (!showChatSessions) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Let any React Aria / HeroUI modal handle Escape first.
      if (document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      handleCloseSessions();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showChatSessions]);

  // this loads chat history and defines answers so we know the laoding state
  const loadChatHistory = async (): Promise<Answer[]> => {
    const requestedOption = selectedOption;
    if (!selectedOption) {
      return [];
    }
    const pendingNewSession = isPendingNewSession(selectedOption);
    // For a pending-new-session case the bar is preserved across navigation
    // via a per-case sessionStorage snapshot (the only continuity source —
    // chat history can't help because no query has been submitted yet).
    const pendingPreservedScopes = pendingNewSession
      ? loadPendingNewSessionScopes(selectedOption)
      : null;
    let formattedAnswers: Answer[] = [];

    // Restoration precedence:
    //   1. `fromHistory` (the loaded session's last-answer scopes)
    //   2. `handoff` (the pending-new-session snapshot for fresh threads
    //      with no chat history yet)
    //   3. Empty.
    const applyLockerForCase = (
      fromHistory: ScopesObject | null | undefined,
      handoff: ScopesObject | null,
    ) => {
      if (fromHistory) {
        const merged = scopesObjectFromAnswerForLockerReuse(fromHistory, emptyScopes());
        setScopesAndFiles(merged);
        applyPersistedContentCategoryState(merged);
        return;
      }
      if (handoff) {
        setScopesAndFiles(handoff);
        applyPersistedContentCategoryState(handoff);
        return;
      }
      const merged = emptyScopes();
      setScopesAndFiles(merged);
      applyPersistedContentCategoryState(merged);
    };

    // Auth/cell must be ready before we take a generation slot. Early exits that
    // used to bump generation could supersede an in-flight load and leave
    // `answers` undefined forever (stuck on "Loading case history…").
    const accessTokenForLoad = realAccessTokenRef.current;
    const idTokenForLoad = idTokenRef.current;
    const cellId = resolveCellIdFromIdToken(idTokenForLoad);

    // Parked off-screen (CSAM dashboard, work products, fullscreen). Skip the
    // assistant-service round-trip — a 403 here surfaces the Next error overlay
    // on views that do not show chat.
    if (!assistantViewVisible) {
      if (requestedOption === selectedOptionRef.current && answersRef.current === undefined) {
        answersRef.current = [];
        setAnswers([]);
        setHistoryScrollPositioned(true);
      }
      return answersRef.current ?? [];
    }

    // Pending-new-session is a UI-only state: chat history is deliberately
    // hidden in favour of a fresh empty thread, and the bar is rehydrated
    // from the per-case sessionStorage snapshot. The fetch result would be
    // discarded, so skip the round-trip entirely — this effect fires on every
    // case-switch back to a pending case.
    if (pendingNewSession) {
      if (requestedOption === selectedOptionRef.current) {
        applyLockerForCase(null, pendingPreservedScopes);
        optimisticPendingRef.current = null;
        optimisticPendingIdentityRef.current = null;
        answersRef.current = [];
        setAnswers([]);
        setHistoryScrollPositioned(true);
        setSessionId(null);
        setMainChatSessionId(null);
        setPreviousSessionId(sessionId);
        if (selectedOption !== previousSelectedOption) {
          setPreviousSelectedOption(selectedOption);
        }
      }
      return [];
    }

    if (!accessTokenForLoad || !cellId) {
      // Token may not yet carry custom:cell_id; leave answers undefined so the
      // auth-ready retry effect runs again when tokens arrive.
      return mergeOptimisticPending([]);
    }

    const generation = ++loadChatHistoryGenerationRef.current;
    // A load is stale if a newer load superseded it (generation) OR the active
    // case changed out from under it (e.g. this closure was captured by the
    // load for a now-abandoned case). Either way it must not write state.
    const isStaleLoad = () =>
      generation !== loadChatHistoryGenerationRef.current ||
      requestedOption !== selectedOptionRef.current;

    const commitAnswers = (
      nextAnswers: Answer[],
      lockerSource: ScopesObject | null | undefined,
    ): Answer[] => {
      if (isStaleLoad()) {
        return mergeOptimisticPending(nextAnswers);
      }
      const merged = mergeOptimisticPending(nextAnswers);
      const tail = merged.length > 0 ? merged[merged.length - 1] : null;
      if (tail && !tail.pendingPlaceholder) {
        setSessionId(tail.sessionId);
        setMainChatSessionId(tail.sessionId);
      } else if (merged.length === 0) {
        setSessionId(null);
        setMainChatSessionId(null);
      }
      applyLockerForCase(
        tail ? tail.scopesObject : lockerSource,
        pendingPreservedScopes,
      );
      answersRef.current = merged;
      setAnswers(merged);
      setPreviousSessionId(sessionId);
      return merged;
    };

    try {
      const history = await fetchChatHistory({
        cellId,
        accessToken: accessTokenForLoad,
        idToken: idTokenForLoad,
        body: {
          userId: userId,
          caseId: selectedOption,
          ...caseFolderNameBody(caseFolderName),
        },
      });
      if (isStaleLoad()) {
        return mergeOptimisticPending(formattedAnswers);
      }
      const isHistoryArray = Array.isArray(history);
      if (isHistoryArray && history.length > 0) {
        setIsLoading(false);
        formattedAnswers = [];
        const cacheableHistoryItems: AnswerItemFromHistory[] = [];
        for (const item of history as AnswerItemFromHistory[]) {
          if (isTimelineHistoryItem(item)) {
            continue;
          }
          const mapped = mapHistoryItemToAnswer(item, t);
          if (!mapped) {
            continue;
          }
          cacheableHistoryItems.push(item);
          formattedAnswers.push(mapped);
        }
        // Memoise only rows that survived JSON validation so AnswerItem
        // cache hits never replay corrupt `answer` bodies.
        cacheChatHistoryItems(cacheableHistoryItems);
        return commitAnswers(formattedAnswers, formattedAnswers.at(-1)?.scopesObject);
      }
      if (history && typeof history === 'object' && history !== null && 'error' in history) {
        // Server returned an error envelope. Answers stay empty; restore via
        // the same precedence as the no-history branch so a pending-new-session
        // bar still survives a transient backend failure.
        const errObj = history as { error?: unknown };
        const errMsg = typeof errObj.error === 'string' ? errObj.error : 'Unknown error';
        const errorMessage = `Error loading chat history for user ${userId} on ${selectedOption}: ${errMsg}`;
        captureError(errorMessage, new Error(errMsg));
        return commitAnswers([], null);
      }
      return commitAnswers([], null);
    } catch (error) {
      if (!isStaleLoad()) {
        const forbidden = error instanceof AssistantApiError && error.status === 403;
        if (!forbidden) {
          const errorMessage = `Error loading chat history for user ${userId} on ${selectedOption}: ${error}`;
          captureError(errorMessage, error as Error);
        }
      }
      return commitAnswers([], null);
    } finally {
      if (!isStaleLoad() && selectedOption !== previousSelectedOption) {
        setPreviousSelectedOption(selectedOption);
      }
    }
  };

  /**
   * The current render's `loadChatHistory`, for the reads that fire long after
   * the render that scheduled them — the post-POST fallback and the auth-retry
   * chain.
   *
   * `loadChatHistory` is re-created every render, so those sites otherwise hold
   * the closure from whichever render armed them. That matters specifically
   * around an awaited write: `awaitCaseFolderName` resolves by fetching the cases
   * list, which fills the cache `useCaseFolderName` reads and so re-renders
   * this component with a resolved segment — but the send is still holding the
   * pre-fetch closure, whose `caseFolderName` is null. Since every load bumps
   * `loadChatHistoryGenerationRef`, that late segment-less read supersedes the
   * correct token-scoped one the re-render started, and the turn just written to
   * the folder-token partition stays invisible until a full reload.
   */
  const loadChatHistoryRef = useRef(loadChatHistory);
  loadChatHistoryRef.current = loadChatHistory;

  // Apply bookmark / Back / Forward chat session targets from Main nav restore.
  useEffect(() => {
    if (initialChatSessionId === undefined) return;
    if (!selectedOption) return;

    const targetSessionId = initialChatSessionId;
    if (lastAppliedNavSessionRef.current === targetSessionId) {
      onNavChatSessionApplied?.();
      return;
    }

    let cancelled = false;

    void (async () => {
      optimisticPendingRef.current = null;
      optimisticPendingIdentityRef.current = null;
      autoSnapScrollRef.current = false;
      currentAnswerIndex.current = undefined;

      if (!targetSessionId) {
        if (cancelled) return;
        answersRef.current = undefined;
        setAnswers(undefined);
        needsInitialHistoryScrollRef.current = true;
        setHistoryScrollPositioned(false);
        lastAppliedNavSessionRef.current = targetSessionId;
        setSessionId(null);
        setMainChatSessionId(null);
        setPreviousSessionId('SWAP');
        onNavChatSessionApplied?.();
        return;
      }

      // Wait for token — effect re-runs when realAccessToken arrives. Do not
      // clear answers here or we can stick on "Loading case history…" with no
      // follow-up load if this run is the only one that cleared them.
      if (!realAccessTokenRef.current) {
        return;
      }

      answersRef.current = undefined;
      setAnswers(undefined);
      needsInitialHistoryScrollRef.current = true;
      setHistoryScrollPositioned(false);
      setIsSwapping(true);
      try {
        // Restore-only: do not delete the prior server session.
        const sessionReturned = await swapSessionId(null, targetSessionId);
        if (cancelled) return;

        lastAppliedNavSessionRef.current = targetSessionId;
        setPreviousSessionId('SWAP');
        const resolved = sessionReturned ?? targetSessionId;
        setSessionId(resolved);
        setMainChatSessionId(resolved);
        onNavChatSessionApplied?.();
      } catch (error) {
        if (cancelled) return;
        // Still adopt the target session id so loadChatHistory can run; never
        // leave isSwapping/answers wedged after Back/Forward.
        captureError(
          `Error applying nav chat session ${targetSessionId} for case ${selectedOption}`,
          error as Error,
        );
        lastAppliedNavSessionRef.current = targetSessionId;
        setPreviousSessionId('SWAP');
        setSessionId(targetSessionId);
        setMainChatSessionId(targetSessionId);
        onNavChatSessionApplied?.();
      } finally {
        setIsSwapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    initialChatSessionId,
    selectedOption,
    swapSessionId,
    setMainChatSessionId,
    onNavChatSessionApplied,
    realAccessToken,
  ]);

  useEffect(() => {
    if (selectedOption !== previousSelectedOption || previousSessionId !== sessionId) {
      // forceLoad: true so we load even if sessions panel is still open (setState from case switch is async)
      void loadChatHistory();
      return;
    }
    void loadChatHistory();
    // `caseFolderName` is a dep because it resolves asynchronously: on a cold
    // load this effect can run before the cases list lands, sending no segment and
    // committing an empty thread that the retry effect below then declines to
    // reload (`answers !== undefined`). Re-running when the segment arrives is what
    // makes a hard refresh on a token-bearing case show its history. On a warm case
    // switch the segment resolves in the same render as `selectedOption`, so this
    // still runs once.
  }, [
    selectedOption,
    previousSelectedOption,
    previousSessionId,
    sessionId,
    showChatSessions,
    caseFolderName,
    assistantViewVisible,
  ]);

  // Case switch / Back-Forward can run loadChatHistory before Cognito tokens are
  // ready; that path returns without setting `answers`. Retry until committed,
  // and force an empty thread rather than infinite "Loading case history…".
  useEffect(() => {
    if (!assistantViewVisible || !selectedOption || isSwapping) {
      return;
    }
    if (answers !== undefined) {
      return;
    }
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryLoad = () => {
      if (cancelled) return;
      attempt += 1;
      // Through the ref: a retry that fires seconds after this effect ran must
      // read the partition the current render resolved, not the one the render
      // that armed the chain knew about.
      void loadChatHistoryRef.current().finally(() => {
        if (cancelled) return;
        // Allow commitAnswers from this load to flush before deciding to retry.
        timer = window.setTimeout(() => {
          if (cancelled) return;
          if (answersRef.current !== undefined) return;
          if (attempt < 8) {
            timer = window.setTimeout(tryLoad, 150 * attempt);
            return;
          }
          // Only force-empty once auth/cell are ready. If tokens are still
          // missing, leave `answers` undefined so this effect retries when
          // realAccessToken / idToken arrive (deps below).
          const cellReady = !!resolveCellIdFromIdToken(idTokenRef.current);
          if (!realAccessTokenRef.current || !cellReady) {
            return;
          }
          // Auth was ready but history still did not commit — unwedge UI.
          setAnswers([]);
          setHistoryScrollPositioned(true);
        }, 0);
      });
    };
    tryLoad();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
    // `caseFolderName` is a dep for the same reason as the load effect
    // above, plus one specific to the retry chain: a chain armed before the cases
    // list landed is mid-backoff when the segment resolves, and its next attempt
    // would land on top of the segmented load that resolution just started.
    // Re-running here cancels that chain (cleanup) and restarts it — while
    // `loadChatHistoryRef` keeps even an already-scheduled attempt on the
    // resolved partition, so the two mechanisms cannot disagree.
  }, [selectedOption, realAccessToken, idToken, answers, isSwapping, caseFolderName, assistantViewVisible]);

  const snapToTop = useCallback((index: number, options?: { force?: boolean; behavior?: ScrollBehavior }) => {
    if (!answersContainerRef.current || isFullscreen || index < 0) {
      return;
    }
    if (!options?.force && !autoSnapScrollRef.current) {
      return;
    }
    const element = document.getElementById(`answer-item-${index}`);
    if (!element) {
      return;
    }
    currentAnswerIndex.current = index;
    programmaticSnapRef.current = true;
    element.scrollIntoView({
      block: 'start',
      behavior: options?.behavior ?? 'smooth',
    });
    window.setTimeout(() => {
      programmaticSnapRef.current = false;
    }, 450);
  }, [isFullscreen]);

  // Fullscreen shrinks the answers list to 1×1px (clamping scrollTop). Save/restore so
  // Escape returns the user to the same answer + SourceCard they left.
  useEffect(() => {
    const wasFullscreen = prevChatIsFullscreenRef.current;
    prevChatIsFullscreenRef.current = isFullscreen;
    const el = answersContainerRef.current;

    if (!wasFullscreen && isFullscreen && el) {
      fullscreenChatScrollRestoreRef.current = el.scrollTop;
      return;
    }

    if (wasFullscreen && !isFullscreen && el && fullscreenChatScrollRestoreRef.current != null) {
      const top = fullscreenChatScrollRestoreRef.current;
      fullscreenChatScrollRestoreRef.current = null;
      programmaticSnapRef.current = true;
      const restore = () => {
        el.scrollTop = top;
      };
      restore();
      requestAnimationFrame(() => {
        restore();
        requestAnimationFrame(() => {
          restore();
          window.setTimeout(() => {
            programmaticSnapRef.current = false;
          }, 100);
        });
      });
    }
  }, [isFullscreen]);

  const getCurrentAnswerIndexFromScroll = useCallback((): number => {
    const container = answersContainerRef.current;
    const indices = getRenderableAnswerIndices(answersRef.current);
    if (!container || indices.length === 0) {
      return currentAnswerIndex.current ?? 0;
    }

    const scrollTop = container.scrollTop;
    let bestIndex = indices[0];

    for (const index of indices) {
      const anchor = document.getElementById(`answer-item-${index}`);
      if (!anchor) continue;
      const top = getAnswerAnchorOffsetTop(anchor, container);
      // Last answer whose start is at or above the viewport top owns the current position.
      if (top <= scrollTop + 40) {
        bestIndex = index;
      } else {
        break;
      }
    }

    return bestIndex;
  }, []);

  /**
   * After scroll settles, snap to the answer with the highest share of itself
   * visible in the viewport. Users can fling across several items; settle lands
   * on whichever answer currently dominates. Tall answers use min(height,
   * viewHeight) so filling the screen counts as fully covered.
   */
  const findSnapIndexByVisibility = useCallback((): number | null => {
    const container = answersContainerRef.current;
    const indices = getRenderableAnswerIndices(answersRef.current);
    if (!container || indices.length === 0) {
      return currentAnswerIndex.current ?? null;
    }

    const scrollTop = container.scrollTop;
    const viewHeight = container.clientHeight;
    const viewBottom = scrollTop + viewHeight;

    type RowMetrics = {
      index: number;
      top: number;
      height: number;
      visiblePx: number;
      visibleRatio: number;
    };
    const allRows: RowMetrics[] = [];

    for (const index of indices) {
      const anchor = document.getElementById(`answer-item-${index}`);
      if (!anchor) continue;
      const top = getAnswerAnchorOffsetTop(anchor, container);
      const row = anchor.parentElement;
      const height = row?.getBoundingClientRect().height ?? 0;
      if (height <= 0) continue;
      const bottom = top + height;
      const visiblePx = Math.max(0, Math.min(viewBottom, bottom) - Math.max(scrollTop, top));
      const coverageBasis = Math.min(height, viewHeight);
      allRows.push({
        index,
        top,
        height,
        visiblePx,
        visibleRatio: coverageBasis > 0 ? visiblePx / coverageBasis : 0,
      });
    }

    if (allRows.length === 0) {
      return null;
    }

    const first = allRows[0];
    const last = allRows[allRows.length - 1];

    // Nothing visible (e.g. deep in bottom padding) — snap to the nearest edge.
    if (allRows.every((row) => row.visiblePx <= 0)) {
      return (scrollTop >= last.top ? last : first).index;
    }

    let best: RowMetrics | null = null;
    for (const row of allRows) {
      if (row.visiblePx <= 0) continue;
      if (
        !best
        || row.visibleRatio > best.visibleRatio
        || (row.visibleRatio === best.visibleRatio && row.visiblePx > best.visiblePx)
      ) {
        best = row;
      }
    }

    if (!best) {
      return (scrollTop >= last.top ? last : first).index;
    }

    // Dominant answer is already current — allow peeks / reading tall answers
    // without pulling back to the snap anchor.
    if (best.index === currentAnswerIndex.current) {
      return null;
    }

    return best.index;
  }, []);

  const navigateAnswerByDelta = useCallback((delta: -1 | 1) => {
    const container = answersContainerRef.current;
    const indices = getRenderableAnswerIndices(answersRef.current);
    if (!container || indices.length === 0) return;

    const current =
      currentAnswerIndex.current !== undefined && indices.includes(currentAnswerIndex.current)
        ? currentAnswerIndex.current
        : getCurrentAnswerIndexFromScroll();
    const position = indices.indexOf(current);

    // When scrolling up inside a tall answer, first snap to that answer's start.
    if (delta === -1 && current != null) {
      const anchor = document.getElementById(`answer-item-${current}`);
      if (anchor) {
        const top = getAnswerAnchorOffsetTop(anchor, container);
        if (container.scrollTop > top + 24) {
          snapToTop(current, { force: true });
          return;
        }
      }
    }

    const nextPosition = Math.max(
      0,
      Math.min(indices.length - 1, (position === -1 ? 0 : position) + delta),
    );
    snapToTop(indices[nextPosition], { force: true });
  }, [getCurrentAnswerIndexFromScroll, snapToTop]);

  const clearAutoSnapScroll = useCallback(() => {
    autoSnapScrollRef.current = false;
  }, []);

  const armChatArrowNav = useCallback(() => {
    if (!assistantViewVisible || isFullscreen) return;
    chatOwnsArrowNavRef.current = true;
    const active = document.activeElement;
    // Blur nav focus (including the case Combobox <input>) so ArrowUp/Down
    // reach answer snapping instead of the case switcher.
    if (active instanceof HTMLElement && active.closest('#main-navigation')) {
      active.blur();
    }
  }, [assistantViewVisible, isFullscreen]);

  const disarmChatArrowNav = useCallback(() => {
    chatOwnsArrowNavRef.current = false;
  }, []);

  useEffect(() => {
    if (!assistantViewVisible) {
      disarmChatArrowNav();
    }
  }, [assistantViewVisible, disarmChatArrowNav]);

  useEffect(() => {
    const onPointerDownCapture = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('#main-navigation')) {
        disarmChatArrowNav();
        return;
      }
      if (target.closest('[data-testid="chat"]')) {
        armChatArrowNav();
      }
    };
    // Tabbing into the case Combobox (or other nav controls) must disarm too —
    // pointerdown alone leaves chat owning arrows after keyboard focus moves.
    const onFocusInCapture = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('#main-navigation')) {
        disarmChatArrowNav();
      }
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('focusin', onFocusInCapture, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('focusin', onFocusInCapture, true);
    };
  }, [armChatArrowNav, disarmChatArrowNav]);

  useEffect(() => {
    const el = answersContainerRef.current;
    if (!el) {
      return;
    }
    const onUserScrollIntent = () => {
      armChatArrowNav();
      if (autoSnapScrollRef.current) {
        clearAutoSnapScroll();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (!CHAT_SCROLL_INTENT_KEYS.has(event.key)) {
        return;
      }

      // Arrow up/down navigate answer items only while chat owns arrow nav.
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        if (
          isFullscreen
          || !assistantViewVisible
          || !answersRef.current?.length
          || !chatOwnsArrowNavRef.current
        ) {
          return;
        }
        if (isArrowKeyReservedTarget(event.target)) {
          return;
        }
        event.preventDefault();
        onUserScrollIntent();
        navigateAnswerByDelta(event.key === 'ArrowUp' ? -1 : 1);
        return;
      }

      if (isArrowKeyReservedTarget(event.target)) {
        return;
      }
      if (!el.contains(event.target as Node)) {
        return;
      }
      onUserScrollIntent();
    };
    const onMouseDown = (event: MouseEvent) => {
      // Native scrollbar hits the scroll container, not answer row children.
      if (event.target === el) {
        onUserScrollIntent();
      }
    };
    const listenerOpts: AddEventListenerOptions = { capture: true, passive: false };
    const passiveOpts: AddEventListenerOptions = { capture: true, passive: true };
    el.addEventListener('wheel', onUserScrollIntent, passiveOpts);
    el.addEventListener('touchmove', onUserScrollIntent, passiveOpts);
    window.addEventListener('keydown', onKeyDown, listenerOpts);
    el.addEventListener('mousedown', onMouseDown, passiveOpts);
    return () => {
      el.removeEventListener('wheel', onUserScrollIntent, passiveOpts);
      el.removeEventListener('touchmove', onUserScrollIntent, passiveOpts);
      window.removeEventListener('keydown', onKeyDown, listenerOpts);
      el.removeEventListener('mousedown', onMouseDown, passiveOpts);
    };
  }, [
    answers,
    selectedOption,
    isSwapping,
    isFullscreen,
    assistantViewVisible,
    clearAutoSnapScroll,
    navigateAnswerByDelta,
    armChatArrowNav,
  ]);

  // start polling when the user scrolls to the bottom of the chat answers container;
  // after scrolling settles, snap to the answer with the highest visible coverage.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;

    if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
      loadChatHistory();
    }

    if (programmaticSnapRef.current || autoSnapScrollRef.current || isFullscreen) {
      return;
    }

    if (scrollSnapTimerRef.current) {
      clearTimeout(scrollSnapTimerRef.current);
    }
    scrollSnapTimerRef.current = setTimeout(() => {
      scrollSnapTimerRef.current = null;
      if (programmaticSnapRef.current || autoSnapScrollRef.current) {
        return;
      }
      const snapIndex = findSnapIndexByVisibility();
      if (snapIndex == null) {
        return;
      }
      const containerEl = answersContainerRef.current;
      const anchor = document.getElementById(`answer-item-${snapIndex}`);
      if (!containerEl || !anchor) {
        return;
      }
      const top = getAnswerAnchorOffsetTop(anchor, containerEl);
      if (Math.abs(top - containerEl.scrollTop) <= 8) {
        currentAnswerIndex.current = snapIndex;
        return;
      }
      snapToTop(snapIndex, { force: true });
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (scrollSnapTimerRef.current) {
        clearTimeout(scrollSnapTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!answers) {
      return;
    }
    const sessionChanged = currentSessionId.current !== sessionId;
    const followingNewTurn =
      autoSnapScrollRef.current
      && (optimisticPendingRef.current != null
        || optimisticPendingIdentityRef.current != null);
    if (sessionChanged) {
      currentSessionId.current = sessionId;
      // Session id can catch up from the assistant response while a submit is
      // in flight — that is not a user session switch; keep follow mode on.
      if (!followingNewTurn) {
        currentAnswerIndex.current = undefined;
        needsInitialHistoryScrollRef.current = true;
      }
    }
    const scrollPosition = answersContainerRef.current?.scrollTop || 0;
    const scrollHeight = answersContainerRef.current?.scrollHeight || 0;
    const clientHeight = answersContainerRef.current?.clientHeight || 0;
    const scrollHeightMinusClientHeight = scrollHeight - clientHeight;
    if (scrollPosition >= scrollHeightMinusClientHeight) {
      const indices = getRenderableAnswerIndices(answers);
      currentAnswerIndex.current =
        indices.length > 0 ? indices[indices.length - 1] : answers.length - 1;
    }

    // Case load / Back-Forward / session restore: always land on the last
    // answer-item (most recent question), not mid-thread.
    if (needsInitialHistoryScrollRef.current && !followingNewTurn) {
      const positionInitialHistoryScroll = (attempt = 0) => {
        if (scrollToLastAnswerItem(answers)) {
          needsInitialHistoryScrollRef.current = false;
          setHistoryScrollPositioned(true);
          return;
        }
        if (attempt < 12) {
          requestAnimationFrame(() => positionInitialHistoryScroll(attempt + 1));
          return;
        }
        // DOM never produced anchors — reveal at container bottom anyway.
        const container = answersContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        needsInitialHistoryScrollRef.current = false;
        setHistoryScrollPositioned(true);
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => positionInitialHistoryScroll());
      });
      return;
    }

    if (!autoSnapScrollRef.current) {
      return;
    }
    const targetIndex =
      currentAnswerIndex.current === undefined
        ? (getRenderableAnswerIndices(answers).at(-1) ?? answers.length - 1)
        : currentAnswerIndex.current;
    snapToTop(targetIndex);
  }, [answers, sessionId, snapToTop, scrollToLastAnswerItem]);

  const processSourcesFromChat = async () => {
    const newSourcesFromChat: MediaSource[] = [];
    const newProcessedSourcesMap: { [key: number]: MediaSource[] } = {};

    try {
      answers?.forEach(async (answer: Answer, index: number) => {
        const answerProcessedSources: MediaSource[] = [];
        answer.sources?.forEach(async (source: Source) => {
          const sourceObject = getUiSourceFromChatSource(source);
          answerProcessedSources.push(sourceObject);
          newSourcesFromChat.push(sourceObject);
        });
        newProcessedSourcesMap[index] = answerProcessedSources;
      });
      const dedupedSources = dedupeSources(newSourcesFromChat);
      setSourcesFromChat(dedupedSources);
      // setProcessedSources(newSourcesFromChat);
      setProcessedSourcesMap(newProcessedSourcesMap);
    } catch (error) {
      captureError('Error processing sources from chat', error as Error);
    } finally {
      // do nothing
    }
  };

  useEffect(() => {
    processSourcesFromChat();
  }, [answers, sessionId, selectedOption]);

  const releaseQuestionBarSubmitHold = useCallback(() => {
    holdQuestionBarExpandedRef.current = false;
    setHoldQuestionBarExpanded(false);
    pendingSubmitEpochRef.current = null;
  }, []);

  const releaseQuestionBarSubmitHoldIfCurrent = useCallback(
    (submitGeneration: number, options?: { clearQuery?: boolean; collapse?: boolean }) => {
      if (
        !holdQuestionBarExpandedRef.current ||
        submitGenerationRef.current !== submitGeneration
      ) {
        return false;
      }
      releaseQuestionBarSubmitHold();
      if (options?.clearQuery !== false) {
        setQuery('');
      }
      if (options?.collapse !== false) {
        setIsExpanded(false);
      }
      return true;
    },
    [releaseQuestionBarSubmitHold],
  );

  const beginChatRequest = useCallback(() => {
    chatRequestsInFlightRef.current += 1;
    setIsLoading(true);
  }, []);

  const endChatRequest = useCallback(() => {
    chatRequestsInFlightRef.current = Math.max(0, chatRequestsInFlightRef.current - 1);
    if (chatRequestsInFlightRef.current === 0) {
      setIsLoading(false);
    }
  }, []);

  const handleAnswerItemDisplayed = useCallback((epochTimeSecs: number) => {
    const pendingIndex = answersRef.current?.findIndex(
      (answer) => answer.epochTimeSecs === epochTimeSecs,
    ) ?? -1;
    if (pendingIndex >= 0 && autoSnapScrollRef.current) {
      requestAnimationFrame(() => {
        snapToTop(pendingIndex);
      });
    }
    if (!holdQuestionBarExpandedRef.current) {
      return;
    }
    if (pendingSubmitEpochRef.current !== epochTimeSecs) {
      return;
    }
    releaseQuestionBarSubmitHold();
    setQuery('');
    setIsExpanded(false);
  }, [releaseQuestionBarSubmitHold, snapToTop]);

  const onQuerySubmit = async (
    query: string,
    scopesAndFiles: ScopesObject,
    userId: string,
    sessionId: string | null,
    useDeepDive: boolean = false,
    useMobile: boolean = false,
  ) => {
    const submitToMobileAgent =
      isMobileAnalysisAvailableForCase(featureFlags, selectedOption, personalFilesCaseId) &&
      useMobile;
    const trimmedQuery = query.trim();
    const submitGeneration = ++submitGenerationRef.current;
    setSubmitError(null);
    holdQuestionBarExpandedRef.current = true;
    setHoldQuestionBarExpanded(true);
    setIsExpanded(true);
    // Snapshot the case this submission belongs to. If the user switches cases
    // while POST / refresh is in flight, async cleanup must not clobber the new case.
    const submittedOption = selectedOption;
    const caseStillActive = () => selectedOptionRef.current === submittedOption;
    if (typeof window !== 'undefined' && selectedOption) {
      sessionStorage.removeItem(assistantSkipScopeRestoreStorageKey(selectedOption));
      clearPendingNewSession(selectedOption);
      clearPendingNewSessionScopes(selectedOption);
    }
    const files = scopesAndFiles.files;
    const scopes = scopesAndFiles.scopes;
    const excludedFiles = scopesAndFiles.excludedFiles;
    const excludedScopes = scopesAndFiles.excludedScopes;
    const selectedContentCategories =
      (scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0
        ? scopesAndFiles.evidenceCategoryScopes!
        : scopesAndFiles.contentCategories || [];

    const contentCategories = [
      ...new Set(
        selectedContentCategories
          .map((fileType) => fileTypeToCategoryMap[fileType])
          .filter((c): c is string => typeof c === 'string' && c.length > 0)
          .map((c) => c.toLowerCase()),
      ),
    ];

    const excludedContentCategories = [
      ...new Set(
        excludedContentCategoriesFromScopes(
          scopesAndFiles.excludedEvidenceCategoryScopes,
        )
          .filter((c): c is string => typeof c === 'string' && c.length > 0)
          .map((c) => c.toLowerCase()),
      ),
    ];

    try {
      let tenant = null;
      if (accessToken) {
        const tokenInfo = getTokenInfo(accessToken);
        tenant = tokenInfo?.['custom:tenant'] || null;
      }
      const cellId = resolveCellIdFromIdToken(idToken);
      if (!cellId || !realAccessToken) {
        throw new Error('Missing cell session for assistant chat.');
      }

      // The picker stores `data/bulk/`-prefixed paths because downstream
      // provenance lookups (file-info, file-status, /api/proxy) key off that
      // shape. The assistant-service indexes raw `searchable_path`, so strip
      // the prefix at the wire boundary.
      const searchableFiles = files.map(toSearchablePath);
      const searchableScopes = scopes.map(toSearchablePath);

      // Mobile analysis agent (assistant-service ``mobile: true`` → MOBILE
      // pipeline). A single matching extraction is sent as
      // ``mobileAcquisitionId``. No mobile path omits the id so the server
      // analyzes the case. A scoped path that is missing, ambiguous, or
      // unscanned refuses the send — omitting the id would widen that phone
      // to every acquisition on the case.
      // Kick the scan off without awaiting: the question bar is already
      // locked, and awaiting here would leave the thread blank until
      // mobile-service pages acquisition_map. The pending row paints first;
      // the id is merged onto the body just before POST (with caseFolderName).
      //
      // Resolved once and shared: the scan needs the folder segment too, because
      // ``acquisition_map.case_name`` holds the segment and not the display name
      // (see ``resolveMobileAcquisitionIds``). Chaining off the same promise keeps
      // the cases-list await off the paint path and avoids resolving twice.
      const caseFolderNamePromise = awaitCaseFolderName(selectedCaseForAssistant);
      const mobileAcquisitionPromise = submitToMobileAgent
        ? caseFolderNamePromise.then((caseFolderName) =>
            resolveMobileAcquisitionIds({
              cellId,
              accessToken: realAccessToken,
              idToken,
              caseFolderName,
              // Narrowing fallback when the segment has not resolved; correct for
              // a legacy case, and refused as `unmatched` for a UUID-folder one.
              caseDisplayName: selectedCaseForAssistant,
              scopedPaths: [...searchableFiles, ...searchableScopes],
            }),
          )
        : Promise.resolve({ status: 'unscoped' as const });

      // English omits the field so assistant-service keeps today's English
      // answer. Greek, Spanish, and German are the names that service accepts.
      const responseLanguage = responseLanguageForLocale(readStoredLocale());
      const body: any = {
        query: trimmedQuery,
        deepDive: useDeepDive,
        ...(submitToMobileAgent && { mobile: true, requestFeedback: true }),
        threatReport: false,
        selectedCase: selectedCaseForAssistant,
        // `caseFolderName` / `mobileAcquisitionId` are filled in just before
        // the POST, once they have been awaited — see below.
        files: searchableFiles,
        scopes: searchableScopes,
        excludedFiles: excludedFiles.map(toSearchablePath),
        excludedScopes: excludedScopes.map(toSearchablePath),
        userId: userId,
        sessionId: sessionId,
        ...(responseLanguage ? { responseLanguage } : {}),
        regularRagAnswerVerificationEnabled: !!featureFlags?.regularRagAnswerVerification,
        deepDiveAnswerVerificationEnabled: !!featureFlags?.deepDiveAnswerVerification,
        answerVerificationDebugOutputEnabled: !!featureFlags?.answerVerificationDebugOutput,
        ...(featureFlags?.contentCategories && contentCategories.length > 0 && { contentCategories }),
        ...(featureFlags?.contentCategories &&
          excludedContentCategories.length > 0 && { excludedContentCategories }),
      };
      if (tenant) {
        body.tenant = tenant;
      }

      // Match enrich-event and other assistant flows: pass display identity for
      // contextual follow-ups (answer selection → "Tell me more about: …").
      if (/^Tell me more about:/i.test(trimmedQuery)) {
        const caseApiName = getCaseInternalName(selectedOption).trim();
        if (caseApiName && caseApiName !== '*') {
          body.caseName = caseApiName;
        }
        const displayUser = (userName ?? '').trim();
        if (displayUser) {
          body.userName = displayUser;
        }
      }

      currentAnswerIndex.current = undefined;
      autoSnapScrollRef.current = true;

      // Prefer explicit UI include scopes; fall back to contentCategories when
      // the submit payload only carried include keys under that field.
      const optimisticIncludedScopes =
        (scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0
          ? [...(scopesAndFiles.evidenceCategoryScopes ?? [])]
          : [...(scopesAndFiles.contentCategories ?? [])];
      const optimisticExcludedScopes = [
        ...(scopesAndFiles.excludedEvidenceCategoryScopes ?? []),
      ];
      const optimisticCategoryFields = evidenceCategoryScopeFieldsWithWireTokens(
        optimisticIncludedScopes,
        optimisticExcludedScopes,
        !!scopesAndFiles.evidenceAllFileTypes,
      );

      // History rows carry `request_type` so AnswerItem can badge the turn as
      // DeepDive / Mobile. Seed the same value on the optimistic row, otherwise
      // the badge only appears once the first history fetch lands.
      const optimisticRequestType = useDeepDive
        ? 'deep_dive'
        : submitToMobileAgent
          ? 'mobile'
          : null;

      const optimisticTurn: Answer = {
        question: trimmedQuery,
        answerText: encodePreliminaryMessageStrings(['Initializing...']),
        sources: [],
        sessionId,
        timeSaved: 0,
        epochTimeSecs: -Date.now(),
        scopesObject: {
          scopes: [...scopes],
          files: [...files],
          excludedScopes: [...excludedScopes],
          excludedFiles: [...excludedFiles],
          ...optimisticCategoryFields,
        },
        pendingPlaceholder: true,
        ...(optimisticRequestType
          ? {
              resultForFeedback: {
                question: trimmedQuery,
                request_type: optimisticRequestType,
                status: 'initializing',
              } as AnswerItemFromHistory,
            }
          : {}),
      };
      optimisticPendingRef.current = optimisticTurn;
      optimisticPendingIdentityRef.current = null;
      pendingSubmitEpochRef.current = optimisticTurn.epochTimeSecs;
      pendingHistoryRefreshEpochRef.current = optimisticTurn.epochTimeSecs;
      setAnswers((prev) => [...(prev ?? []), optimisticTurn]);
      beginChatRequest();
      try {
        // Awaited rather than read off the render: a send that raced the cases
        // list would have assistant-service key `chat_key` under the display
        // name, while the history effects — re-run once the segment resolves —
        // read the folder-token partition, so the turn disappears on refresh.
        // Resolved here, after the optimistic row is on screen, so a stale cases
        // cache (or a mobile-service acquisition scan) costs a background
        // request and not a blank thread.
        const [caseFolderName, mobileAcquisition] = await Promise.all([
          caseFolderNamePromise,
          mobileAcquisitionPromise,
        ]);
        Object.assign(body, caseFolderNameBody(caseFolderName));
        if (submitToMobileAgent) {
          const submit = mobileAcquisitionIdForSubmit(mobileAcquisition);
          if (!submit.ok) {
            if (caseStillActive()) {
              const refusedEpoch = optimisticTurn.epochTimeSecs;
              optimisticPendingRef.current = null;
              optimisticPendingIdentityRef.current = null;
              setAnswers((prev) => {
                const next = (prev ?? []).filter((row) => row.epochTimeSecs !== refusedEpoch);
                answersRef.current = next;
                return next;
              });
              releaseQuestionBarSubmitHoldIfCurrent(submitGeneration, { clearQuery: false });
              setSubmitError(submit.error);
            } else {
              releaseQuestionBarSubmitHoldIfCurrent(submitGeneration);
            }
            endChatRequest();
            return;
          }
          if (submit.mobileAcquisitionId != null) {
            body.mobileAcquisitionId = submit.mobileAcquisitionId;
          }
        }
        const result = await postAssistantChat({
          cellId,
          accessToken: realAccessToken,
          idToken,
          body,
        });
        // The user may have switched cases while POST `/chat` was in flight.
        // The case-switch effect has already reset state for the new case, so
        // skip the optimistic bookkeeping and refresh for this abandoned turn.
        if (!caseStillActive()) {
          releaseQuestionBarSubmitHoldIfCurrent(submitGeneration);
          endChatRequest();
          return;
        }
        const pendingIdentity =
          parsePendingTurnIdentityFromAssistantResponse(result);
        optimisticPendingIdentityRef.current = pendingIdentity;
        if (pendingIdentity) {
          const sessionIdFromResult =
            typeof result?.sessionId === 'string'
              ? result.sessionId
              : optimisticTurn.sessionId;
          optimisticPendingRef.current = {
            ...optimisticTurn,
            epochTimeSecs: pendingIdentity.epochTimeSecs,
            pendingPlaceholder: false,
            sessionId: sessionIdFromResult,
            resultForFeedback: {
              epoch_time_secs: pendingIdentity.epochTimeSecs,
              chat_key: pendingIdentity.chatKey,
              question: trimmedQuery,
              status:
                typeof result?.status === 'string' ? result.status : 'inprogress',
              ...(optimisticRequestType
                ? { request_type: optimisticRequestType }
                : {}),
            } as AnswerItemFromHistory,
          };
          let optimisticRowReplaced = false;
          setAnswers((prev) => {
            if (!prev?.length) {
              return prev;
            }
            const next = [...prev];
            const pendingIndex = next.findIndex(
              (row) =>
                row.pendingPlaceholder &&
                row.epochTimeSecs === optimisticTurn.epochTimeSecs,
            );
            if (pendingIndex >= 0) {
              next[pendingIndex] = optimisticPendingRef.current!;
              optimisticRowReplaced = true;
            }
            answersRef.current = next;
            return next;
          });
          if (optimisticRowReplaced) {
            pendingSubmitEpochRef.current = pendingIdentity.epochTimeSecs;
            pendingHistoryRefreshEpochRef.current = pendingIdentity.epochTimeSecs;
          }
          void refreshChatHistorySessions?.({
            expectedSessionId: sessionIdFromResult ?? sessionId,
            expectedQuestionEpoch: pendingIdentity.epochTimeSecs,
          });
          if (caseStillActive()) {
            await refreshPendingHistoryItem(pendingIdentity);
          }
        } else if (caseStillActive()) {
          // 2xx response with no `epoch_time_secs`/`chat_key`: the service
          // accepted the call but did not register a turn. Flag it so this
          // otherwise-silent path is diagnosable, then fall back to a history
          // reload.
          captureWarning(
            `Assistant chat POST returned no pending-turn identity for user ${userId} on case ${submittedOption}`,
          );
          // Ref, not the closure: the awaited segment resolution above may have
          // re-rendered with a segment this closure predates, and a segment-less
          // read here would supersede that render's load (every load bumps the
          // generation) and show an empty thread for the case just written to.
          await loadChatHistoryRef.current();
        }
        if (caseStillActive()) {
          optimisticPendingRef.current = null;
          optimisticPendingIdentityRef.current = null;
          const pendingEpoch = pendingSubmitEpochRef.current;
          const hasMatchingPendingRow =
            pendingEpoch != null
            && (answersRef.current ?? []).some(
              (row) => row.epochTimeSecs === pendingEpoch,
            );
          if (!hasMatchingPendingRow) {
            releaseQuestionBarSubmitHoldIfCurrent(submitGeneration);
          }
        }
        endChatRequest();
        return;
      } catch (error) {
        // POST `/chat` failed — most often this means the request never reached
        // the assistant service (network error, timeout, WAF/gateway rejection,
        // expired token). Log the real reason; without this the failure is
        // invisible (the outer catch almost never runs because the throw
        // originates here).
        captureError(
          `Assistant chat POST failed for user ${userId} on case ${submittedOption}: ${describeAssistantError(error)}`,
          error as Error,
        );
        if (caseStillActive()) {
          // Convert the optimistic "Initializing…" row into a visible error so
          // the thread (and any prior history) is not silently wiped to an empty
          // view. Do NOT call loadChatHistory() here: if the backend is
          // unreachable that fetch fails too and commits an empty answers list,
          // erasing the whole thread — the exact "flash then empty" symptom.
          const failedEpoch = optimisticTurn.epochTimeSecs;
          optimisticPendingRef.current = null;
          optimisticPendingIdentityRef.current = null;
          setAnswers((prev) => {
            if (!prev?.length) {
              return prev;
            }
            const next = prev.map((row) =>
              row.epochTimeSecs === failedEpoch
                ? {
                    ...row,
                    answerText: RETRY_MESSAGE,
                    pendingPlaceholder: false,
                    // status: 'error' marks the row terminal so AnswerItem
                    // renders it statically and never enters the poll loop.
                    resultForFeedback: {
                      epoch_time_secs: failedEpoch,
                      question: trimmedQuery,
                      status: 'error',
                      ...(optimisticRequestType
                        ? { request_type: optimisticRequestType }
                        : {}),
                    } as AnswerItemFromHistory,
                  }
                : row,
            );
            answersRef.current = next;
            return next;
          });
          releaseQuestionBarSubmitHoldIfCurrent(submitGeneration);
        }
        endChatRequest();
        return;
      }
    } catch (error) {
      const errorMessage = 'Error querying TimePilot:';
      captureError(errorMessage, error as Error);
      optimisticPendingRef.current = null;
      optimisticPendingIdentityRef.current = null;
      // Surface the failure instead of silently clearing the box, and KEEP the
      // typed question so the user can retry without retyping. This path is hit
      // when the submit throws before the request is sent — e.g. a missing/
      // expired cell session — which previously just reset the input to empty
      // with no answer and no error.
      const failure = describeSubmitFailure(error, t);
      releaseQuestionBarSubmitHoldIfCurrent(submitGeneration, { clearQuery: failure.clearQuery });
      setSubmitError(failure.errorMessage);
      endChatRequest();
    }
  };

  const handleClearThread = async () => {
    if (isResetting) {
      setMainChatSessionId(null);
      setFullscreenMediaItem(null);
      return;
    }
    try {
      setIsResetting(true);
      setShowFolders(false);
      const cellId = resolveCellIdFromIdToken(idToken);
      if (cellId && realAccessToken) {
        // Awaited for the same reason as the send path: a DELETE keyed on the
        // display name leaves the UUID partition intact, so the "cleared" thread
        // reappears on the next load.
        const clearedCaseFolderName = await awaitCaseFolderName(selectedCaseForAssistant);
        await deleteChatHistory({
          cellId,
          accessToken: realAccessToken,
          idToken,
          body: {
            userId: userId,
            caseId: selectedOption,
            ...caseFolderNameBody(clearedCaseFolderName),
          },
        });
      }
      if (typeof window !== 'undefined' && selectedOption) {
        sessionStorage.removeItem(assistantSkipScopeRestoreStorageKey(selectedOption));
      }
      lastAssistantSyncedTailEpochRef.current = null;
      lastAssistantSyncedCaseRef.current = null;
      optimisticPendingRef.current = null;
      optimisticPendingIdentityRef.current = null;
      autoSnapScrollRef.current = false;
      currentAnswerIndex.current = undefined;
      currentSessionId.current = null;
      releaseQuestionBarSubmitHold();
      // Snapshot the current bar into per-case sessionStorage so it
      // survives both the immediate reload AND any later case-switch round
      // trip while the user is still drafting on this fresh thread. The
      // snapshot is cleared when the user submits a query (chat history
      // takes over) or switches to a different session via history.
      const currentLocker = scopesAndFilesRef.current;
      markPendingNewSession(selectedOption);
      savePendingNewSessionScopes(selectedOption, currentLocker);
      applyPersistedContentCategoryState(currentLocker);
      setIsResetting(false);
      setSessionId(null);
      setPreviousSessionId(null);
      setMainChatSessionId(null);
      setAnswers(undefined);
      setTriggerPromptLibrary(0);
    } catch (error) {
      setIsResetting(false);
      console.error('Error clearing chat history:', error);
      // TODO: Handle error (e.g., show an error message to the user)
    } finally {
      setIsResetting(false);
    }
  };

  const handleRedo = async (question: string) => {
    setIsExpanded(true);
    // Wait for the expansion animation to complete
    await new Promise(resolve => setTimeout(resolve, 500));
    setQuery(question.trim());
    textareaRef.current?.focus();
    if (textareaRef.current) {
      textareaRef.current.value = question.trim();
    }
  };

  const setCurrentScopesAndFiles = useCallback(
    (fromAnswer: ScopesObject) => {
      if (typeof window !== 'undefined' && selectedOption) {
        sessionStorage.removeItem(assistantSkipScopeRestoreStorageKey(selectedOption));
      }
      setIsExpanded(true);
      // Apply this answer's scopes as a replacement. Merge against emptyScopes so
      // omitted category fields (common on older history rows) clear composer
      // exclusions/includes instead of keeping the bar's current filters.
      const next = scopesObjectFromAnswerForLockerReuse(fromAnswer, emptyScopes());
      handleSetScopesAndFiles(next);
      applyPersistedContentCategoryState(next);
    },
    [selectedOption, handleSetScopesAndFiles, applyPersistedContentCategoryState],
  );

  const useOldChatNewEvidenceLocker = shouldUseOldChatNewEvidenceLocker(featureFlags);
  const useMigratedQuestionBar = isQuestionBarMigrationEnabled(featureFlags);
  const QuestionBarView = useMigratedQuestionBar ? QuestionBarMigrated : QuestionBar;

  useEffect(() => {
    if (assistantViewSelected) return;
    // Chat intentionally remains mounted behind Work Products so its history
    // and open source card survive navigation. CSS visibility does not stop
    // HTML media playback, so explicitly silence every player owned by Chat.
    pauseMediaElements(chatRootRef.current);
  }, [assistantViewSelected]);

  const chatShell = (
    <div
      ref={chatRootRef}
      data-testid="chat"
      className={`z-1 box-border flex flex-col relative justify-center items-center
        text-['#f9f9f9'] font-inter overflow-x-hidden
        ${isFullscreen || !assistantViewVisible ? 'pointer-events-none invisible h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-y-hidden' :
          featureFlags?.enableMediaAndSourceShare && selectedOption ?
          'h-[calc(100vh-150px)] sm:h-[calc(100vh-186px)]' :
          'h-[calc(100vh-26px)] sm:h-[calc(100vh-113px)]'}`}
    >
      {!isSwapping && !!answers && !selectedOption && (
        <div className="flex flex-col items-center justify-center mt-[-100px] sm:mt-[50px]">
          <div id="logo" className="flex flex-row items-center justify-center gap-2 sm:gap-4 leading-normal tracking-normal mq480:flex-wrap mb-14 sm:mb-20">
              <img
              className="relative object-contain w-[300px] sm:w-[800px] h-[96px] sm:h-[280px] mb-[-50px]"
              alt={t('chat.logoAlt')}
              src="/tp-logotype.png"
              />
          </div>
          <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`mt-4 sm:mt-0 text-medium sm:text-2xl py-2 sm:py-6 sm:px-8 px-3 ${GLOW_CTA_BUTTON_CLASS}`}>{t('chat.gettingStarted.button')}</Button>
        </div>
      )}
      <ChatGettingStartedModal isOpen={isModalOpen} onOpenChange={setIsModalOpen} />
      {!isSwapping && !!answers && selectedOption && !isFullscreen && typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {showChatSessions && (
              <>
                <motion.div
                  key="session-history-backdrop"
                  aria-hidden
                  className={`${SIDE_PANEL_BACKDROP_CLASS} ${SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS} bg-black/20`}
                  initial={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                  animate={{ opacity: 1, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
                  exit={{ opacity: 0, backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  onPointerDown={() => handleCloseSessions()}
                />
                <SidePanelShell
                  key="session-history-panel"
                  side="left"
                  aria-modal={false}
                  aria-label={t('chat.sessions.title')}
                  className={`${SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS} max-w-[95vw]`}
                  style={{ width: CHAT_SESSIONS_PANEL_WIDTH_PX }}
                >
                  <ChatSessions
                    caseId={selectedOption}
                    userId={userId}
                    sessionId={sessionId}
                    chatSessions={chatHistorySessions}
                    switchToSession={switchToSession}
                    closeSessions={handleCloseSessions}
                    onRefresh={refreshChatHistorySessions}
                    showMobileAnalysis={isMobileAnalysisAgentEnabled(featureFlags)}
                  />
                </SidePanelShell>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}
      {!!isSwapping && !!answers && (
        <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>{t('chat.sessions.switching')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
        </div>
      )}
      {!selectedOption && (
        <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>{t('chat.selectCase')}</div>
        </div>
      )}
      {!!selectedOption
        && (!answers || (!historyScrollPositioned && answers.length > 0))
        && !isSwapping && (
        <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>{t('chat.sessions.loadingCaseHistory')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
        </div>
      )}
      {!isSwapping && !!answers && selectedOption && (
        <div
          id="chatAnswersContainer"
          ref={answersContainerRef}
          onScroll={handleScroll}
          aria-hidden={!historyScrollPositioned && answers.length > 0}
          className={`box-border
            ${!historyScrollPositioned && answers.length > 0 ? 'invisible pointer-events-none' : ''}
            ${isFullscreen ?
              '-z-10 w-[1px] max-w-[1px] max-h-[1px] h-[1px] overflow-y-hidden overflow-x-hidden' :
              'w-[calc(100vw-20px)] z-[10008] overflow-x-hidden custom-scrollbar2 flex flex-col items-center h-full overflow-y-auto sm:flex-grow'}
            ${!answers || answers.length === 0 ? '' : featureFlags?.enableMediaAndSourceShare ? (isTinyViewport ? 'pb-[300px]' : 'pb-[495px]') : (isTinyViewport ? 'pb-[260px]' : 'pb-[445px]')}
            ${(!!answers && answers.length > 0) || (!!preliminaryResult) ? '' : 'justify-center'}
          `}
        >
          {(!answers || answers.length === 0) && <Welcome />}
          {((!!answers && answers.length > 0) || (!!preliminaryResult)) && (
            <div>
              {!!answers && answers.length > 0 && answers.map((answer, index) => {
                if (!isRenderableAnswer(answer)) {
                  return null;
                }
                const pendingSubmitEpoch = pendingSubmitEpochRef.current;
                const isPendingSubmitRow =
                  holdQuestionBarExpanded &&
                  pendingSubmitEpoch != null &&
                  answer.epochTimeSecs === pendingSubmitEpoch;
                return (
                <div
                  key={answerRowKey(answer, index)}
                  data-answer-index={index}
                  className="box-border flex flex-col items-center justify-center gap-0 pb-6 opacity-100 transition-opacity duration-500"
                  style={{ width: '100vw' }}
                >
                  <div
                    id={`answer-item-${index}`}
                    className='flex h-[1px] w-[1px] background-transparent scroll-mt-0'
                    aria-hidden="true"
                  >
                  </div>
                  <div
                    className="box-border flex items-center justify-center pb-6 opacity-100 transition-opacity duration-500"
                    style={{ width: '100vw' }}
                  >
                    <AnswerItem
                      key={answerRowKey(answer, index)}
                      userId={userId}
                      caseId={selectedOption}
                      answerIndex={index}
                      question={answer.question.replace(/^strands-/, '').replace(/^threat-report-/, '')}
                      answerText={answer.answerText}
                      sources={processedSourcesMap[index] || answer.sources}
                      epochTimeSecs={answer.epochTimeSecs}
                      pendingPlaceholder={!!answer.pendingPlaceholder}
                      onRedo={handleRedo}
                      addToTimeline={addToTimeline}
                      generateTimelineFromAnswer={generateTimelineFromAnswer}
                      timelineGenerationStatus={
                        timelineGenerationStatuses[`${selectedOption}:${answer.epochTimeSecs}`] ?? 'idle'
                      }
                      addMultipleToTimeline={addMultipleToTimeline}
                      applyAutogeneratedTimelineFromAnswer={applyAutogeneratedTimelineFromAnswer}
                      isAutogeneratedTimelineAppliedForAnswer={isAutogeneratedTimelineAppliedForAnswer}
                      isAutogeneratedTimelineApplyingForAnswer={isAutogeneratedTimelineApplyingForAnswer}
                      isEditableTimeline={isEditableTimeline}
                      featureFlags={featureFlags || {}}
                      resultForFeedback={answer.resultForFeedback}
                      attachments={answer.attachments}
                      scopesObject={answer.scopesObject}
                      setCurrentScopesAndFiles={setCurrentScopesAndFiles}
                      addSourceToScope={addSourceToScope}
                      onSendToQuery={setQuery}
                      onFilterEvidence={setEvidenceFilter}
                      setIsExpanded={setIsExpanded}
                      extractionJob={extractionJobs.find(job => Number(job.epochTimeSecs) === Number(answer.epochTimeSecs)) || {status: 'dummy_status'}}
                      createEventsFromEntityEvents={createEventsFromEntityEvents}
                      getEventsFromEntityService={getEventsFromEntityService}
                      snapToTop={() => snapToTop(index, { force: true })}
                      onDisplayed={
                        isPendingSubmitRow
                          ? () => handleAnswerItemDisplayed(answer.epochTimeSecs)
                          : undefined
                      }
                      onAnswerSettled={() => {
                        if (pendingHistoryRefreshEpochRef.current !== answer.epochTimeSecs) {
                          return;
                        }
                        pendingHistoryRefreshEpochRef.current = null;
                        void refreshChatHistorySessions?.({
                          expectedSessionId: sessionId,
                          expectedQuestionEpoch: answer.epochTimeSecs,
                        });
                      }}
                    />
                  </div>
                </div>
                );
              })}
            </div>
          )}
          <div className={`z-[10007] box-border flex flex-col items-center justify-center w-screen ${isSwapping || !answers ? 'w-1 h-1' : ''}`}>
            <QuestionBarView
              assistantViewSelected={assistantViewSelected}
              useNewEvidenceLockerModal={useOldChatNewEvidenceLocker}
              assistantViewVisible={assistantViewVisible}
              caseId={selectedOption}
              onSubmit={onQuerySubmit}
              userId={userId}
              sessionId={sessionId}
              answers={answers || []}
              hasChatHistory={(answers?.length || 0) > 0}
              onClearThread={handleClearThread}
              isLoading={isLoading}
              isResetting={isResetting}
              setQuery={setQuery}
              query={query}
              textareaRef={textareaRef}
              isExpanded={
                !answers || answers.length === 0
                  ? true
                  : isExpanded || holdQuestionBarExpanded
              }
              holdSubmitVisualLock={holdQuestionBarExpanded}
              setIsExpanded={setIsExpanded}
              collapseSources={() => setShowSources(false)}
              scopesAndFiles={scopesAndFiles}
              setScopesAndFiles={handleSetScopesAndFiles}
              showFolders={showFolders}
              setShowFolders={setShowFolders}
              allFileTypes={allFileTypes}
              setAllFileTypes={setAllFileTypes}
              activeFileTypes={activeFileTypes}
              setActiveFileTypes={setActiveFileTypes}
              evidenceFilter={evidenceFilter}
              addSourceToScope={addSourceToScope}
              addToTimeline={addToTimeline}
              featureFlagsProp={featureFlags}
              triggerPromptLibrary={triggerPromptLibrary}
              onAssistantEvidenceFilterCleared={() => {
                if (typeof window !== 'undefined' && selectedOption) {
                  sessionStorage.setItem(assistantSkipScopeRestoreStorageKey(selectedOption), '1');
                }
              }}
            />
            {submitError && (
              <div
                role="alert"
                className="mt-2 max-w-[640px] px-3 py-2 text-sm text-red-300 bg-red-950/40 border border-red-800/60 rounded-md"
              >
                {submitError}
              </div>
            )}
          </div>
          {!isSwapping && !!answers && !isFullscreen && (!answers || answers.length === 0) && isPromptFlagOn(featureFlags?.suggestedPrompts) && (
            <PromptSuggestionsNew
              onSelectPrompt={(promptQuery) => {
                setQuery(promptQuery);
                const textarea = textareaRef.current;
                if (textarea) {
                  textarea.value = promptQuery;
                  textarea.focus();
                }
              }}
              showPromptLibrary={isPromptFlagOn(featureFlags?.promptLibrary)}
              onOpenPromptLibrary={() => setTriggerPromptLibrary((prev) => prev + 1)}
            />
          )}
        </div>
      )}
    </div>
  );

  if (!useOldChatNewEvidenceLocker && !useMigratedQuestionBar) {
    return chatShell;
  }

  return (
    <ChatEvidenceLockerProvider
      key={selectedOption}
      caseId={selectedOption}
      scopesAndFiles={scopesAndFiles}
      setScopesAndFiles={handleSetScopesAndFiles}
      showFolders={showFolders}
      setShowFolders={setShowFolders}
      allFileTypes={allFileTypes}
      setAllFileTypes={setAllFileTypes}
      activeFileTypes={activeFileTypes}
      setActiveFileTypes={setActiveFileTypes}
    >
      {chatShell}
    </ChatEvidenceLockerProvider>
  );
};

ChatComponent.displayName = 'Chat';

/**
 * Memoized so unrelated `Main` re-renders (polling, fullscreen toggles, time
 * saved, etc.) don't cascade into the chat tree. Effective only because the
 * callback props from `Main` are wrapped in `useStableCallback` and the
 * `featureFlags` prop has a stable identity (guarded merge in `main.tsx`).
 */
export const Chat = React.memo(ChatComponent);
